import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * <AlertsBadge /> — small Nav badge that shows the current count of NEW
 * CVE alerts for the signed-in tenant.
 *
 *  - Polls `/api/cve-alerts/stats` every 60 seconds while mounted.
 *  - Auto-hides for Free users (the endpoint returns 402 and we just stop).
 *  - Click navigates to `/alerts`.
 *
 * Designed to slot between <GlobalSearch /> and <UserMenu /> in the Nav.
 */
export const AlertsBadge = ({ user }) => {
    const [count, setCount] = useState(null);
    const [eligible, setEligible] = useState(true);

    useEffect(() => {
        if (!user) {
            setEligible(false);
            return;
        }
        // Beta testers, Pro, MSP, admin → eligible. Free → never poll.
        const plan = user.subscription_plan || "free";
        const isPaid =
            user.is_beta_tester ||
            user.role === "admin" ||
            ["pro", "msp"].includes(plan);
        if (!isPaid) {
            setEligible(false);
            return;
        }

        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await axios.get(`${API}/cve-alerts/stats`, {
                    withCredentials: true,
                });
                if (!cancelled) setCount(data?.total_new ?? 0);
            } catch (err) {
                if (err?.response?.status === 402) {
                    if (!cancelled) setEligible(false);
                } else if (!cancelled) {
                    // Quietly swallow network errors — badge is non-critical.
                    setCount(null);
                }
            }
        };
        tick();
        const id = setInterval(tick, 60_000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [user]);

    if (!eligible) return null;

    const display = count === null ? null : count;
    const hasNew = (display ?? 0) > 0;

    return (
        <NavLink
            to="/alerts"
            className={({ isActive }) =>
                `relative inline-flex items-center justify-center h-9 w-9 rounded-md border transition-colors duration-200 ${
                    isActive
                        ? "border-accent-cyan/70 text-accent-cyan bg-accent-cyan/10"
                        : hasNew
                        ? "border-accent-amber/40 text-accent-amber hover:bg-accent-amber/5"
                        : "border-white/[0.06] text-text-muted hover:border-white/15 hover:text-text-primary"
                }`
            }
            aria-label={
                hasNew
                    ? `${display} new CVE alert${display === 1 ? "" : "s"}`
                    : "CVE alerts"
            }
            title={hasNew ? `${display} new CVE alert${display === 1 ? "" : "s"}` : "CVE alerts"}
            data-testid="nav-alerts-badge"
        >
            <ShieldAlert className="w-[18px] h-[18px]" aria-hidden="true" />
            {hasNew && (
                <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-accent-amber text-canvas text-[10px] font-bold leading-[18px] text-center"
                    data-testid="nav-alerts-count"
                >
                    {display > 99 ? "99+" : display}
                </span>
            )}
        </NavLink>
    );
};

export default AlertsBadge;
